import type { BundleTestResult } from '@flux/shared';
import type { JSX } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { cn } from '@/lib/cn.js';

/**
 * Rendu partagé du résultat de « Tester » (`02` §8) : les lignes qui partiraient
 * chez Martelet pour ce SKU et cette quantité.
 *
 * Vit à part parce que deux écrans l'affichent — le tiroir de la liste et la
 * section intégrée à la fiche d'un bundle — et qu'ils doivent en dire
 * exactement la même chose.
 */
function originLabel(origin: { type: 'line' | 'bundle'; bundleSku: string | null }): string {
  return origin.type === 'bundle' ? `Bundle ${origin.bundleSku ?? ''}` : 'Ligne d’origine';
}

export interface BundleExpansionResultProps {
  result: BundleTestResult;
  /**
   * `true` dans un panneau sans marge (`PageSection flush`) : le tableau va
   * d'un bord à l'autre, séparé par un filet. Sinon (tiroir), il porte son
   * propre cadre.
   */
  flush?: boolean;
}

export function BundleExpansionResult({
  result,
  flush = false,
}: BundleExpansionResultProps): JSX.Element {
  return (
    <div className={cn('flex flex-col', flush ? 'border-t border-border' : 'gap-3')}>
      <p className={cn('text-sm', flush && 'px-5 py-3')}>
        {result.matched
          ? `Ce SKU se décompose${result.label ? ` — ${result.label}` : ''} :`
          : 'Aucun bundle pour ce SKU : il partira tel quel chez Martelet.'}
      </p>
      <div
        className={
          flush
            ? 'border-t border-border'
            : 'overflow-hidden rounded-lg border border-border bg-card'
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Référence</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead className="text-right">Quantité</TableHead>
              <TableHead className="text-right">Prix unitaire</TableHead>
              <TableHead className="text-right">Remise</TableHead>
              <TableHead>Origine</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.lines.map((line, index) => (
              <TableRow key={`${line.reference}-${line.sku}-${String(index)}`}>
                <TableCell className="whitespace-nowrap">{line.reference}</TableCell>
                <TableCell className="whitespace-nowrap">{line.sku}</TableCell>
                <TableCell className="text-right tabular-nums">{line.quantity}</TableCell>
                <TableCell className="text-right tabular-nums">{line.unitPrice}</TableCell>
                <TableCell className="text-right tabular-nums">{line.discount}</TableCell>
                <TableCell className="whitespace-nowrap">{originLabel(line.origin)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {result.warnings.length > 0 ? (
        <ul
          className={cn(
            'list-disc pl-4 text-sm text-status-orange-fg',
            flush && 'border-t border-border py-3 pl-9 pr-5',
          )}
        >
          {result.warnings.map((warning, index) => (
            <li key={`${warning.code}-${String(index)}`}>{warning.message}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
